<template>
  <form-page-template>
    <div class="uc-form">
      <volunteer-form v-if="this.userSelection === 'volunteer'" />
      <student-form v-else-if="this.userSelection === 'student'" />

      <div v-else>
        <h1 class="uc-form-header">{{ welcomeMessage }}</h1>
        <p
          v-if="isReferred"
          class="uc-form-text"
          v-html="$t('signUp.isReferred.text', { firstName: firstName })"
        />

        <p v-else class="uc-form-text">
          {{ $t('signUp.notReferred.text') }}
        </p>

        <div v-if="isReferred" class="uc-column items-center">
          <button
            class="uc-form-button"
            type="submit"
            @click.prevent="selectStudent()"
          >
            {{ $t('signUp.isReferred.submitButton') }}
          </button>

          <a
            target="_blank"
            rel="noopener noreferrer"
            class="link"
            href="https://upchieve.org/students"
          >
            {{ $t('signUp.isReferred.link') }}
          </a>
        </div>
        <div v-else>
          <p class="uc-form-text bold">{{ $t('signUp.notReferred.text-1') }}</p>
          <div class="btn-card-container">
            <button class="btn-card student" @click.prevent="selectStudent">
              <div class="img-container">
                <student-avatar class="img"></student-avatar>
              </div>
              <p class="text">{{ $t('signUp.notReferred.text-2') }}</p>
            </button>
            <button class="btn-card volunteer" @click.prevent="selectVolunteer">
              <div class="img-container">
                <volunteer-avatar class="img"></volunteer-avatar>
              </div>
              <p class="text">{{ $t('signUp.notReferred.text-3') }}</p>
            </button>
          </div>
        </div>
        <i18n path="signUp.notReferred.text-4" tag="p" class="uc-form-text">
          <router-link class="uc-link" :to="`/${$i18n.locale}/login`">{{
            $t('signUp.notReferred.login')
          }}</router-link>
        </i18n>
      </div>
    </div>
  </form-page-template>
</template>

<script>
import FormPageTemplate from '@/components/FormPageTemplate.vue'
import StudentForm from './StudentForm'
import VolunteerForm from './VolunteerForm'
import NetworkService from '@/services/NetworkService'
import AnalyticsService from '@/services/AnalyticsService'
import StudentAvatar from '@/assets/student-avatar.svg'
import VolunteerAvatar from '@/assets/volunteer-avatar.svg'
import { EVENTS } from '@/consts'
import { capitalize } from 'lodash'
import i18n from '@/i18n'

export default {
  name: 'signup-view',
  components: {
    FormPageTemplate,
    StudentForm,
    VolunteerForm,
    StudentAvatar,
    VolunteerAvatar,
  },
  data() {
    return {
      userSelection: null,
      isReferred: false,
      referredBy: {},
    }
  },

  async created() {
    this.$store.dispatch('app/hideNavigation')

    const referralCode = this.$route.query.referral
    if (referralCode) {
      window.localStorage.setItem('upcReferredByCode', referralCode)
      AnalyticsService.captureEvent(EVENTS.USER_VISITED_REFERRAL_LINK, {
        referralCode,
      })
      this.isReferred = true

      const {
        data: { user },
      } = await NetworkService.getReferredBy(referralCode)

      if (!user) {
        this.isReferred = false
        window.localStorage.removeItem('upcReferredByCode')
      } else this.referredBy = user
    }

    if (this.$route.params)
      if (this.$route.params.userType === 'student')
        this.userSelection = 'student'
      else if (this.$route.params.userType === 'volunteer')
        this.userSelection = 'volunteer'
  },
  computed: {
    welcomeMessage() {
      if (this.isReferred && this.referredBy)
        return this.$root.$t('signUp.isReferred.welcomeMessage', {
          firstName: this.firstName,
        })

      return this.$root.$t('signUp.notReferred.welcomeMessage')
    },
    firstName() {
      if (this.referredBy && this.referredBy.firstname)
        return capitalize(this.referredBy.firstname)
      else return ''
    },
  },

  methods: {
    selectVolunteer() {
      this.$router.push(`/${i18n.locale}/sign-up/volunteer`)
      this.userSelection = 'volunteer'
    },
    selectStudent() {
      this.$router.push(`/${i18n.locale}/sign-up/student`)
      this.userSelection = 'student'
    },
  },
}
</script>

<style lang="scss" scoped>
.uc-form-header {
  display: block;
  font-size: 36px;
  text-align: center;
}

.uc-form-text {
  margin-top: 25px;
  text-align: center;

  &.bold {
    font-weight: 500;
  }
}

.btn-card-container {
  @include flex-container(row, space-evenly);
  margin: 40px 0;

  @include breakpoint-below('small') {
    @include flex-container(column, center, center);
    @include child-spacing(top, 15px);
    margin: 20px 0;
  }
}

.btn-card {
  @include flex-container(column, space-between);
  background: white;
  border: none;
  border-radius: 8px;
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.2);
  height: 160px;
  overflow: hidden;
  padding: 0;
  position: relative;
  top: 0;
  transition: top 0.2s ease-in-out;
  width: 170px;

  &:hover {
    top: -5px;
  }

  &.volunteer {
    .img-container {
      background: #a5e9ff;

      .img {
        height: 85%;
        top: 8px;
      }
    }
  }

  &.student {
    .img-container {
      background: #f9bef9;
    }
  }

  .img-container {
    flex-grow: 1;
    position: relative;
    width: 100%;

    .img {
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }
  }

  .text {
    background: white;
    font-size: 14px;
    font-weight: 500;
    padding: 10px;
    margin: 0;
    width: 100%;
  }
}

a.link {
  color: $c-information-blue;
  font-size: 18px;
  font-weight: 500;
  margin: 20px 0 50px 0;
  text-align: center;

  &:hover {
    color: #103a90;
    text-decoration: none;
  }
}
</style>
